/*
  Nook and Wave: the Social share composer (the user, 2026-10-29). Lives ONLY in the Social row under a product's
  Dimensions and in the discreet "Share this design" link on a special-order design's page (snippets/nw-social.liquid);
  nothing else on the site reads or changes because of it. Every picture is drawn here, live, in the visitor's browser
  from the listing photo: nothing is stored anywhere, no server, no keys.

  A visitor (or we) picks a finish and one of its listing photos, a SIZE -- each platform's recommended picture shape:
  Pinterest pin 2:3 (1000 x 1500), Instagram post 4:5 (1080 x 1350), Story / Reel 9:16 (1080 x 1920), square 1:1
  (1080 x 1080), Facebook / link 1.91:1 (1200 x 630) -- a STYLE (just the photo; Cubei's pin layouts: photo and band,
  two views, dark frame; dimensions; the finish feature with the sheet's colour panel; "see it in your space" for a
  piece with the AR viewer), light TAGS laid on any style (the finish swatch, the view-in-your-space tag) and a
  SEASON skin laid over any style (two each for Halloween, Thanksgiving, the winter holidays and New Year's; none by
  default). Then a caption (several written versions, editable) and where to share.

  SHARING: Pinterest, Instagram, Facebook and Share all hand the picture FILE to the device's share sheet (phones,
  and computers whose browser can share files -- Chrome / Edge on Windows, Safari on a Mac -- where any installed app
  appears); only "just the photo" to Pinterest opens Pinterest's own Save window, since that photo is already on the
  store. Where the browser cannot share files the picture is downloaded and the caption and link copied (Facebook:
  its share window with the link). A DECK of up to 20 pictures in one size goes out together the same way -- an
  Instagram carousel post when shared to Instagram on a phone.

  TRACING (privacy-safe): every share makes a SHARE ID (base36 time + 4 random characters) carried in the link as
  utm_id, with utm_source = the platform, utm_medium = social_share, utm_campaign = the page, utm_content = style,
  finish, size (and season). One GA4 event, nw_share, is sent with the same ID (only when the visitor's privacy choice
  allows analytics and the store's own Google tag has set its _ga cookie) -- so in BigQuery the visits arriving on a
  share link join back to the session that made it (when, which picture, device, approximate city -- what Analytics
  already records). No personal data, no exact location, nothing in the link about the person sharing.
  `python tools/google_lib.py shares` lists them.

  PARTNERS (the GoAffPro affiliate program, docs/goaffpro/SETUP.md): a partner enters their code once in the composer
  ("Partner? Enter your code"); that device remembers it and every share link carries it as ?ref=<code>. Nothing else
  sets it -- a visitor who came by a partner's link shares without the partner's code. Optional "Partner link on the
  picture" (off by default) prints the link on the image. nw_share records the code.
*/
(function () {
  'use strict';
  var NS = window.NWSocial = window.NWSocial || {};
  if (NS.ready) return;
  NS.ready = true;

  var CREAM = '#f7f2ea', INK = '#2e2621', MUTED = '#6f645b', META = '#5c5047';
  var GA = 'G-BH0688ZYF1', DECK_MAX = 20;
  var DOT = { 'Endless Elm': '#beaf93', Summit: '#604435', Alderon: '#876f57', Moorland: '#7c654d', White: '#f3f1ea', 'White with natural maple edge': '#f3f1ea', Bamboo: '#d8c08a', 'Wright Walnut': '#5b4334', 'Casual Chestnut': '#7d5a43' };
  var WORDS = { 'Endless Elm': 'a light elm woodgrain', Summit: 'a deep dark-brown woodgrain', Alderon: 'a warm medium-brown woodgrain', Moorland: 'a rich brown woodgrain', White: 'white with a natural maple edge', 'White with natural maple edge': 'white with a natural maple edge', Bamboo: 'solid bamboo' };
  var CODE = { 'Endless Elm': 'Arauco Prism WF474', Summit: 'Arauco Prism WF480', Alderon: 'Arauco Prism WF482', Moorland: 'Arauco Prism WF481', White: 'Arauco W100 white, natural maple edge', 'White with natural maple edge': 'Arauco W100 white, natural maple edge' };

  var FORMATS = [
    { key: 'pin', w: 1000, h: 1500, name: 'Pinterest', ratio: '2:3', note: 'Pinterest pin: 2:3, 1000 × 1500 px (Pinterest’s recommended size)' },
    { key: 'post', w: 1080, h: 1350, name: 'Instagram post', ratio: '4:5', note: 'Instagram and Facebook feed post: 4:5, 1080 × 1350 px (the recommended portrait size; a carousel keeps one size)' },
    { key: 'story', w: 1080, h: 1920, name: 'Story / Reel', ratio: '9:16', note: 'Instagram and Facebook Stories and Reels, Pinterest Idea pins: 9:16, 1080 × 1920 px' },
    { key: 'square', w: 1080, h: 1080, name: 'Square', ratio: '1:1', note: 'Square post: 1:1, 1080 × 1080 px' },
    { key: 'wide', w: 1200, h: 630, name: 'Facebook / link', ratio: '1.91:1', note: 'Facebook and link previews: 1.91:1, 1200 × 630 px' }
  ];
  var STYLES = [
    { key: 'plain', name: 'Just the photo' },
    { key: 'band', name: 'Photo and band' },
    { key: 'two', name: 'Two views' },
    { key: 'frame', name: 'Dark frame' },
    { key: 'dims', name: 'Dimensions' },
    { key: 'finish', name: 'The finish' },
    { key: 'space', name: 'See it in your space', ar: true }
  ];
  var SKINS = [
    { key: 'none', name: 'None' },
    { key: 'moonlit', name: 'Moonlit', season: 'halloween' },
    { key: 'pumpkin', name: 'Pumpkin patch', season: 'halloween' },
    { key: 'leaves', name: 'Falling leaves', season: 'thanksgiving' },
    { key: 'harvest', name: 'Harvest', season: 'thanksgiving' },
    { key: 'evergreen', name: 'Evergreen', season: 'holidays' },
    { key: 'snow', name: 'First snow', season: 'holidays' },
    { key: 'midnight', name: 'Midnight gold', season: 'newyear' },
    { key: 'champagne', name: 'Champagne', season: 'newyear' }
  ];
  var SEASON_NAME = { halloween: 'Halloween', thanksgiving: 'Thanksgiving', holidays: 'Holidays', newyear: 'New Year' };
  var CAPTIONS = [
    { key: 'short', name: 'Short' },
    { key: 'story', name: 'The story' },
    { key: 'dims', name: 'Dimensions' },
    { key: 'finish', name: 'The finish' },
    { key: 'tags', name: 'With hashtags' },
    { key: 'none', name: 'No caption' }
  ];
  NS.FORMATS = FORMATS; NS.STYLES = STYLES; NS.SKINS = SKINS; NS.CAPTIONS = CAPTIONS;
  // the New Year a skin wishes: from September on, the coming one
  function newYear() { var d = new Date(); return d.getMonth() >= 8 ? d.getFullYear() + 1 : d.getFullYear(); }

  // ---------------------------------------------------------------- fonts and pictures
  var fontsP = null;
  NS.loadFonts = function (f) {
    if (fontsP) return fontsP;
    if (!f || typeof FontFace === 'undefined') return (fontsP = Promise.resolve());
    var faces = [
      new FontFace('NW Fraunces', 'url(' + f.fraunces + ')', { weight: '400', style: 'normal' }),
      new FontFace('NW Fraunces', 'url(' + f.frauncesItalic + ')', { weight: '400', style: 'italic' }),
      new FontFace('NW DM Sans', 'url(' + f.dmsans + ')', { weight: '400' }),
      new FontFace('NW DM Sans', 'url(' + f.dmsansMedium + ')', { weight: '500' })
    ];
    fontsP = Promise.all(faces.map(function (x) { return x.load().then(function (y) { document.fonts.add(y); }).catch(function () {}); }));
    return fontsP;
  };
  var imgs = {};
  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (!imgs[url]) imgs[url] = new Promise(function (ok) {
      var im = new Image(); im.crossOrigin = 'anonymous'; im.decoding = 'async';
      im.onload = function () { ok(im); }; im.onerror = function () { ok(null); };
      im.src = url;
    });
    return imgs[url];
  }

  // ---------------------------------------------------------------- drawing helpers (as Cubei's pin builder)
  function cover(ctx, im, x, y, w, h, px, py) {
    if (!im) { ctx.fillStyle = '#d9cfc2'; ctx.fillRect(x, y, w, h); return; }
    px = px == null ? 0.5 : px; py = py == null ? 0.5 : py;
    var s = Math.max(w / im.width, h / im.height), sw = w / s, sh = h / s;
    ctx.drawImage(im, (im.width - sw) * px, (im.height - sh) * py, sw, sh, x, y, w, h);
  }
  function font(ctx, size, o) {
    o = o || {};
    ctx.font = (o.italic ? 'italic ' : '') + (o.weight || 400) + ' ' + size + 'px ' + (o.serif ? '"NW Fraunces", Georgia, serif' : '"NW DM Sans", "Helvetica Neue", Arial, sans-serif');
    try { ctx.letterSpacing = ((o.track || 0) * size) + 'px'; } catch (e) { /* older browsers: no tracking */ }
  }
  function fit(ctx, text, size, maxW, o) { var s = size; font(ctx, s, o); while (s > 8 && ctx.measureText(text).width > maxW) { s -= 1; font(ctx, s, o); } return s; }
  // the home page's wordmark: "Nook" "&" "Wave", Fraunces 400, the & italic at 85%, tracking -0.025em
  function wordmark(ctx, size, x, y, align, color) {
    var parts = [['Nook ', false, 1], ['&', true, 0.85], [' Wave', false, 1]];
    var widths = parts.map(function (p) { font(ctx, size, { serif: true, italic: p[1], track: -0.025 }); return ctx.measureText(p[0]).width; });
    var total = widths.reduce(function (a, b) { return a + b; }, 0);
    var cx = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
    ctx.textAlign = 'left'; ctx.fillStyle = color;
    parts.forEach(function (p, i) { font(ctx, size, { serif: true, italic: p[1], track: -0.025 }); ctx.globalAlpha = p[2]; ctx.fillText(p[0], cx, y); cx += widths[i]; });
    ctx.globalAlpha = 1;
  }
  function wrap(ctx, text, maxW) {
    var out = [], line = '';
    String(text || '').split(/\s+/).forEach(function (w) { var t = line ? line + ' ' + w : w; if (line && ctx.measureText(t).width > maxW) { out.push(line); line = w; } else line = t; });
    if (line) out.push(line);
    return out;
  }
  function lines(ctx, text, x, y, maxW, lh, max) { var ls = wrap(ctx, text, maxW); if (max && ls.length > max) { ls = ls.slice(0, max); ls[max - 1] += '…'; } ls.forEach(function (l, i) { ctx.fillText(l, x, y + i * lh); }); return ls.length; }
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }
  function swatchDot(ctx, sw, finish, cx, cy, r, ring) {
    ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.closePath();
    if (sw) { ctx.clip(); cover(ctx, sw, cx - r, cy - r, 2 * r, 2 * r); } else { ctx.fillStyle = DOT[finish] || '#a99'; ctx.fill(); }
    ctx.restore();
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    var light = /^White/.test(finish);
    ctx.lineWidth = Math.max(1, r / 8); ctx.strokeStyle = ring || (light ? 'rgba(46,38,33,0.55)' : 'rgba(0,0,0,0.22)'); ctx.stroke();
  }
  function chip(ctx, sw, finish, x, y, align, color, ring) {
    font(ctx, 26, { weight: 500 }); var tw = ctx.measureText(finish).width, total = 30 + 12 + tw;
    var x0 = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
    swatchDot(ctx, sw, finish, x0 + 15, y - 9, 15, ring);
    ctx.fillStyle = color; ctx.textAlign = 'left'; font(ctx, 26, { weight: 500 }); ctx.fillText(finish, x0 + 42, y);
  }
  function arIcon(ctx, cx, cy, s, color) {
    ctx.save(); ctx.strokeStyle = color; ctx.lineWidth = Math.max(1.5, s * 0.075); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    var h = s / 2, b = s * 0.28;
    [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(function (c) {
      ctx.beginPath(); ctx.moveTo(cx + c[0] * h, cy + c[1] * (h - b)); ctx.lineTo(cx + c[0] * h, cy + c[1] * h); ctx.lineTo(cx + c[0] * (h - b), cy + c[1] * h); ctx.stroke();
    });
    var u = s * 0.2;
    ctx.beginPath();
    ctx.moveTo(cx, cy - u); ctx.lineTo(cx + u, cy - u / 2); ctx.lineTo(cx + u, cy + u / 2); ctx.lineTo(cx, cy + u); ctx.lineTo(cx - u, cy + u / 2); ctx.lineTo(cx - u, cy - u / 2); ctx.closePath();
    ctx.moveTo(cx - u, cy - u / 2); ctx.lineTo(cx, cy); ctx.lineTo(cx + u, cy - u / 2); ctx.moveTo(cx, cy); ctx.lineTo(cx, cy + u);
    ctx.stroke(); ctx.restore();
  }
  function inBox(ctx, x, y, w, h, baseW, baseH, fn, maxK) {
    var k = Math.min(w / baseW, h / baseH); if (maxK) k = Math.min(k, maxK);
    ctx.save(); ctx.translate(x + (w - baseW * k) / 2, y + (h - baseH * k) / 2); ctx.scale(k, k); fn(ctx); ctx.restore();
  }

  // ---------------------------------------------------------------- the band contents (a 1000-wide design box)
  function bandA(t, sw, light) {
    return function (ctx) {
      var ink = light ? CREAM : INK, meta = light ? '#e6ddd1' : META, muted = light ? '#d9cfc2' : MUTED;
      wordmark(ctx, 112, 500, 153, 'center', ink);
      ctx.textAlign = 'center'; ctx.fillStyle = ink; fit(ctx, t.name, 58, 900, { serif: true, track: -0.01 }); ctx.fillText(t.name, 500, 365);
      ctx.fillStyle = meta; fit(ctx, t.size, 26, 900, { track: 0.02 }); ctx.fillText(t.size, 500, 423);
      chip(ctx, sw, t.finish, 500, 479, 'center', ink, light ? CREAM : null);
      ctx.textAlign = 'center'; ctx.fillStyle = muted; font(ctx, 21);
      var n = wrap(ctx, t.label, 720).length; lines(ctx, t.label, 500, 651 - (n - 1) * 29, 720, 29);
      font(ctx, 21, { track: 0.14 }); ctx.fillText('NOOKANDWAVE.COM', 500, 695);
    };
  }
  function bandB(t, sw) {
    return function (ctx) {
      wordmark(ctx, 74, 60, 118, 'left', INK);
      chip(ctx, sw, t.finish, 940, 100, 'right', INK);
      ctx.textAlign = 'left'; ctx.fillStyle = INK; var s = fit(ctx, t.name + ' · ' + t.short, 40, 880, { serif: true, track: -0.01 });
      font(ctx, s, { serif: true, track: -0.01 }); ctx.fillText(t.name, 60, 223);
      var nw = ctx.measureText(t.name).width; ctx.fillStyle = META; font(ctx, Math.round(s * 0.6), { track: 0.02 }); ctx.fillText(' · ' + t.short, 60 + nw + 6, 223);
      ctx.fillStyle = MUTED; font(ctx, 21); lines(ctx, t.label, 60, 268, 880, 29, 2);
    };
  }
  function bandDims(t, sw) {
    return function (ctx) {
      wordmark(ctx, 62, 500, 100, 'center', INK);
      ctx.textAlign = 'center'; ctx.fillStyle = INK; fit(ctx, t.name, 44, 900, { serif: true, track: -0.01 }); ctx.fillText(t.name, 500, 182);
      var d = t.dims || {}, cols = [['WIDTH', d.w], ['HEIGHT', d.h], ['DEPTH', d.d]];
      cols.forEach(function (c, i) {
        var x = 190 + i * 310;
        ctx.textAlign = 'center'; ctx.fillStyle = INK; var v = (c[1] || '–') + '″';
        fit(ctx, v, 92, 290, { serif: true, track: -0.02 }); ctx.fillText(v, x, 340);
        ctx.fillStyle = META; font(ctx, 22, { track: 0.16, weight: 500 }); ctx.fillText(c[0], x, 392);
        if (i) { ctx.strokeStyle = 'rgba(46,38,33,0.18)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(x - 155, 262); ctx.lineTo(x - 155, 400); ctx.stroke(); }
      });
      ctx.textAlign = 'center'; ctx.fillStyle = META; font(ctx, 26, { track: 0.02 });
      if (d.cm) ctx.fillText(d.cm, 500, 458);
      if (t.cubes) { fit(ctx, t.cubes, 24, 900, { track: 0.02 }); ctx.fillText(t.cubes, 500, 506); }
      chip(ctx, sw, t.finish, 500, 572, 'center', INK);
      ctx.fillStyle = MUTED; font(ctx, 21); var n = wrap(ctx, t.label, 760).length; lines(ctx, t.label, 500, 660 - (n - 1) * 29, 760, 29);
      font(ctx, 21, { track: 0.14 }); ctx.fillText('NOOKANDWAVE.COM', 500, 705);
    };
  }
  function bandFinish(t, sw) {
    return function (ctx) {
      ctx.save(); roundRect(ctx, 60, 70, 420, 420, 22); ctx.clip();
      if (sw) cover(ctx, sw, 60, 70, 420, 420); else { ctx.fillStyle = DOT[t.finish] || '#a99'; ctx.fillRect(60, 70, 420, 420); }
      ctx.restore();
      roundRect(ctx, 60, 70, 420, 420, 22); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(46,38,33,0.2)'; ctx.stroke();
      ctx.textAlign = 'left'; ctx.fillStyle = META; font(ctx, 22, { weight: 500, track: 0.16 }); ctx.fillText('THE FINISH', 530, 120);
      ctx.fillStyle = INK; font(ctx, 62, { serif: true, track: -0.01 }); var nl = lines(ctx, t.finish, 530, 196, 420, 66, 3);
      var y = 196 + (nl - 1) * 66 + 50;
      if (t.code) { ctx.fillStyle = META; font(ctx, 23, { track: 0.02 }); lines(ctx, t.code, 530, y, 420, 30, 2); y += wrap(ctx, t.code, 420).length * 30 + 14; }
      if (t.words) { ctx.fillStyle = MUTED; font(ctx, 24); lines(ctx, 'Thermally fused laminate in ' + t.words + '.', 530, y + 10, 420, 33, 4); }
      ctx.fillStyle = INK; fit(ctx, t.name, 36, 880, { serif: true, track: -0.01 }); ctx.textAlign = 'left'; ctx.fillText(t.name, 60, 565);
      wordmark(ctx, 46, 60, 640, 'left', INK);
      ctx.textAlign = 'right'; ctx.fillStyle = MUTED; font(ctx, 21, { track: 0.14 }); ctx.fillText('NOOKANDWAVE.COM', 940, 640);
      ctx.textAlign = 'left'; font(ctx, 19); lines(ctx, t.label, 60, 692, 880, 26, 2);
    };
  }

  // ---------------------------------------------------------------- the season skins
  // each is drawn over the finished template: motifs kept to the photo's edges and corners (the piece itself stays
  // clear), a thin frame round the whole picture, and a greeting in a pill on the photo
  function rng(seed) { var a = seed >>> 0; return function () { a |= 0; a = a + 0x6D2B79F5 | 0; var t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  function pick(r, arr) { return arr[Math.floor(r() * arr.length) % arr.length]; }
  function frame(ctx, W, H, colors, s) {
    var m = Math.round(16 * s);
    ctx.save(); ctx.lineWidth = Math.max(1.5, 3 * s); ctx.strokeStyle = colors[0]; ctx.strokeRect(m, m, W - 2 * m, H - 2 * m);
    if (colors[1]) { var m2 = m + Math.round(9 * s); ctx.lineWidth = Math.max(1, 1.4 * s); ctx.strokeStyle = colors[1]; ctx.strokeRect(m2, m2, W - 2 * m2, H - 2 * m2); }
    ctx.restore();
  }
  function greeting(ctx, P, text, o, top) {
    var s = Math.max(0.55, Math.min(P.w, P.h, 1100) / 1000);
    font(ctx, Math.round(40 * s), { serif: true, italic: true, track: 0.005 }); var tw = ctx.measureText(text).width;
    var h = Math.round(76 * s), w = Math.round(tw + 74 * s), x = P.x + (P.w - w) / 2;
    var y = typeof top === 'number' ? top : top ? P.y + Math.round(34 * s) : P.y + P.h - h - Math.round(34 * s);
    ctx.save(); ctx.shadowColor = 'rgba(0,0,0,0.28)'; ctx.shadowBlur = 22 * s; ctx.shadowOffsetY = 6 * s;
    roundRect(ctx, x, y, w, h, h / 2); ctx.fillStyle = o.bg; ctx.fill(); ctx.restore();
    if (o.ring) { roundRect(ctx, x + 5 * s, y + 5 * s, w - 10 * s, h - 10 * s, (h - 10 * s) / 2); ctx.lineWidth = Math.max(1, 1.6 * s); ctx.strokeStyle = o.ring; ctx.stroke(); }
    ctx.fillStyle = o.fg; ctx.textAlign = 'center'; font(ctx, Math.round(40 * s), { serif: true, italic: true, track: 0.005 }); ctx.fillText(text, x + w / 2, y + h / 2 + 14 * s);
    return { x: x, y: y, w: w, h: h };
  }
  function vignette(ctx, P, color, inner) {
    var g = ctx.createRadialGradient(P.x + P.w / 2, P.y + P.h / 2, Math.min(P.w, P.h) * (inner || 0.35), P.x + P.w / 2, P.y + P.h / 2, Math.hypot(P.w, P.h) * 0.62);
    g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, color);
    ctx.save(); ctx.fillStyle = g; ctx.fillRect(P.x, P.y, P.w, P.h); ctx.restore();
  }
  function edgeFade(ctx, P, color, frac, bottom) {
    var h = P.h * frac, y0 = bottom ? P.y + P.h - h : P.y;
    var g = ctx.createLinearGradient(0, bottom ? P.y + P.h : P.y, 0, bottom ? y0 : P.y + h);
    g.addColorStop(0, color); g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.save(); ctx.fillStyle = g; ctx.fillRect(P.x, y0, P.w, h); ctx.restore();
  }
  // a point near the photo's edges, never in its middle
  function edgePoint(r, P, band) {
    for (var i = 0; i < 20; i++) {
      var x = r(), y = r();
      if (x < band || x > 1 - band || y < band * 0.9) return [P.x + x * P.w, P.y + y * P.h];
    }
    return [P.x + P.w * band * r(), P.y + P.h * r()];
  }
  // -- motifs
  function bat(ctx, x, y, s, rot, color) {
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot || 0); ctx.scale(s, s); ctx.fillStyle = color; ctx.beginPath();
    ctx.moveTo(0, -5); ctx.bezierCurveTo(12, -18, 34, -24, 54, -12);
    ctx.quadraticCurveTo(45, -6, 44, 5); ctx.quadraticCurveTo(36, -1, 28, 6); ctx.quadraticCurveTo(20, 0, 12, 8); ctx.quadraticCurveTo(6, 3, 0, 10);
    ctx.quadraticCurveTo(-6, 3, -12, 8); ctx.quadraticCurveTo(-20, 0, -28, 6); ctx.quadraticCurveTo(-36, -1, -44, 5); ctx.quadraticCurveTo(-45, -6, -54, -12);
    ctx.bezierCurveTo(-34, -24, -12, -18, 0, -5); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.ellipse(0, 1, 6.5, 10, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.moveTo(-5.5, -6); ctx.lineTo(-6.5, -15); ctx.lineTo(-1.5, -9); ctx.lineTo(1.5, -9); ctx.lineTo(6.5, -15); ctx.lineTo(5.5, -6); ctx.closePath(); ctx.fill();
    ctx.restore();
  }
  function moon(ctx, x, y, r) {
    var c = document.createElement('canvas'), m = Math.ceil(r * 3); c.width = c.height = m; var g = c.getContext('2d');
    var grad = g.createRadialGradient(m / 2 - r * 0.3, m / 2 - r * 0.3, r * 0.1, m / 2, m / 2, r);
    grad.addColorStop(0, '#fff6dc'); grad.addColorStop(1, '#ecd49a');
    g.fillStyle = grad; g.beginPath(); g.arc(m / 2, m / 2, r, 0, Math.PI * 2); g.fill();
    g.globalCompositeOperation = 'destination-out'; g.beginPath(); g.arc(m / 2 + r * 0.48, m / 2 - r * 0.22, r * 0.88, 0, Math.PI * 2); g.fill();
    ctx.save(); ctx.shadowColor = 'rgba(255,214,140,0.75)'; ctx.shadowBlur = r * 1.1; ctx.drawImage(c, x - m / 2, y - m / 2); ctx.restore();
  }
  function pumpkin(ctx, x, y, r, hue) {   // x, y: where it sits on the ground
    var cy = y - r * 0.78;
    ctx.save(); ctx.fillStyle = 'rgba(30,15,5,0.28)'; ctx.beginPath(); ctx.ellipse(x, y, r * 1.15, r * 0.16, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();
    var ribs = [[-0.6, 0.62], [0.6, 0.62], [-0.32, 0.8], [0.32, 0.8], [0, 0.86]];
    ribs.forEach(function (rb) {
      var gx = x + rb[0] * r, g = ctx.createRadialGradient(gx - r * 0.15, cy - r * 0.3, r * 0.1, gx, cy, r * 0.95);
      g.addColorStop(0, hue[0]); g.addColorStop(0.7, hue[1]); g.addColorStop(1, hue[2]);
      ctx.fillStyle = g; ctx.beginPath(); ctx.ellipse(gx, cy, r * rb[1] * 0.62, r * 0.78, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(90,35,5,0.35)'; ctx.lineWidth = Math.max(1, r * 0.02); ctx.stroke();
    });
    ctx.save(); ctx.strokeStyle = '#4b3a1d'; ctx.lineWidth = r * 0.13; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(x, cy - r * 0.66); ctx.quadraticCurveTo(x + r * 0.05, cy - r * 0.95, x + r * 0.2, cy - r * 1.02); ctx.stroke();
    ctx.strokeStyle = '#5c6b2e'; ctx.lineWidth = r * 0.035; ctx.beginPath(); ctx.moveTo(x + r * 0.05, cy - r * 0.8);
    ctx.bezierCurveTo(x - r * 0.35, cy - r * 1.05, x - r * 0.4, cy - r * 0.7, x - r * 0.2, cy - r * 0.78); ctx.stroke(); ctx.restore();
  }
  function maple(ctx, x, y, s, rot, color) {
    var pts = [], lobes = [[-90, 1], [-62, 0.5], [-35, 0.92], [-12, 0.45], [12, 0.7], [40, 0.36], [90, 0.24], [140, 0.36], [168, 0.7], [192, 0.45], [215, 0.92], [242, 0.5]];
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.scale(s, s);
    ctx.beginPath();
    lobes.forEach(function (l, i) { var a = l[0] * Math.PI / 180, px = Math.cos(a) * 50 * l[1], py = Math.sin(a) * 50 * l[1]; if (!i) ctx.moveTo(px, py); else ctx.lineTo(px, py); pts.push([px, py]); });
    ctx.closePath(); ctx.fillStyle = color; ctx.shadowColor = 'rgba(40,20,10,0.25)'; ctx.shadowBlur = 6; ctx.shadowOffsetY = 3; ctx.fill(); ctx.shadowColor = 'transparent';
    ctx.strokeStyle = 'rgba(60,25,10,0.35)'; ctx.lineWidth = 1.6; ctx.beginPath();
    [0, 2, 4, 8, 10].forEach(function (i) { ctx.moveTo(0, 8); ctx.lineTo(pts[i][0] * 0.85, pts[i][1] * 0.85); }); ctx.moveTo(0, 8); ctx.lineTo(0, 26); ctx.stroke();
    ctx.restore();
  }
  function oak(ctx, x, y, s, rot, color) {
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.scale(s, s); ctx.beginPath();
    var n = 26, L = 60;
    for (var i = 0; i <= n; i++) { var t = i / n, w = Math.sin(Math.PI * t) * (14 + 7 * Math.cos(t * Math.PI * 9)); var px = -L / 2 + t * L; if (!i) ctx.moveTo(px, -w); else ctx.lineTo(px, -w); }
    for (var j = n; j >= 0; j--) { var t2 = j / n, w2 = Math.sin(Math.PI * t2) * (14 + 7 * Math.cos(t2 * Math.PI * 9 + 0.6)); ctx.lineTo(-L / 2 + t2 * L, w2); }
    ctx.closePath(); ctx.fillStyle = color; ctx.shadowColor = 'rgba(40,20,10,0.25)'; ctx.shadowBlur = 6; ctx.shadowOffsetY = 3; ctx.fill(); ctx.shadowColor = 'transparent';
    ctx.strokeStyle = 'rgba(60,25,10,0.35)'; ctx.lineWidth = 1.4; ctx.beginPath(); ctx.moveTo(-L / 2 - 8, 0); ctx.lineTo(L / 2, 0); ctx.stroke();
    ctx.restore();
  }
  function wheat(ctx, x, y, len, ang, s) {   // from (x, y), leaning ang
    ctx.save(); ctx.translate(x, y); ctx.rotate(ang);
    ctx.strokeStyle = '#a8833a'; ctx.lineWidth = 2.6 * s; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(len * 0.05, -len * 0.5, 0, -len); ctx.stroke();
    for (var k = 0; k < 9; k++) {
      var t = 0.58 + k * 0.045, gy = -len * t, side = k % 2 ? 1 : -1;
      ctx.save(); ctx.translate(side * 5 * s, gy); ctx.rotate(side * 0.42);
      var g = ctx.createLinearGradient(0, -9 * s, 0, 9 * s); g.addColorStop(0, '#e5c06e'); g.addColorStop(1, '#b98c34');
      ctx.fillStyle = g; ctx.beginPath(); ctx.ellipse(0, 0, 4.6 * s, 10 * s, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(160,120,50,0.7)'; ctx.lineWidth = 0.9 * s; ctx.beginPath(); ctx.moveTo(0, -9 * s); ctx.lineTo(side * 4 * s, -26 * s); ctx.stroke();
      ctx.restore();
    }
    ctx.restore();
  }
  function bough(ctx, x, y, len, ang, s, r) {
    ctx.save(); ctx.translate(x, y); ctx.rotate(ang); ctx.lineCap = 'round';
    ctx.strokeStyle = '#3b2a1a'; ctx.lineWidth = 2.2 * s; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(len, 0); ctx.stroke();
    var greens = ['#1f4a33', '#2a5c3f', '#173d2a', '#356b48'];
    for (var t = 0; t < len; t += 3.2 * s) {
      var nl = (14 + 6 * r()) * s * (1 - 0.45 * t / len);
      [-1, 1].forEach(function (side) {
        ctx.strokeStyle = pick(r, greens); ctx.lineWidth = 1.7 * s; ctx.beginPath(); ctx.moveTo(t, 0);
        ctx.lineTo(t + nl * 0.55, side * nl * 0.83); ctx.stroke();
      });
    }
    ctx.restore();
  }
  function berries(ctx, x, y, s, r) {
    for (var i = 0; i < 3; i++) {
      var bx = x + (r() - 0.5) * 14 * s, by = y + (r() - 0.5) * 12 * s, br = (5 + 2 * r()) * s;
      var g = ctx.createRadialGradient(bx - br * 0.35, by - br * 0.35, br * 0.1, bx, by, br);
      g.addColorStop(0, '#e2433f'); g.addColorStop(1, '#8b1219'); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(bx, by, br, 0, Math.PI * 2); ctx.fill();
    }
  }
  function bauble(ctx, x, y, rad, thread, s) {
    ctx.save(); ctx.strokeStyle = 'rgba(200,170,90,0.85)'; ctx.lineWidth = 1.2 * s; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y + thread); ctx.stroke();
    var cy = y + thread + rad;
    ctx.fillStyle = '#b8932f'; ctx.fillRect(x - rad * 0.28, cy - rad - rad * 0.24, rad * 0.56, rad * 0.3);
    var g = ctx.createRadialGradient(x - rad * 0.38, cy - rad * 0.38, rad * 0.08, x, cy, rad);
    g.addColorStop(0, '#fff3c4'); g.addColorStop(0.35, '#e0bd5a'); g.addColorStop(1, '#8a6a1e');
    ctx.shadowColor = 'rgba(0,0,0,0.3)'; ctx.shadowBlur = 10 * s; ctx.shadowOffsetY = 4 * s;
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, cy, rad, 0, Math.PI * 2); ctx.fill(); ctx.restore();
  }
  function snowflake(ctx, x, y, rad, rot, alpha) {
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.strokeStyle = 'rgba(255,255,255,' + alpha + ')'; ctx.lineWidth = Math.max(1, rad * 0.075); ctx.lineCap = 'round';
    ctx.shadowColor = 'rgba(160,190,215,0.6)'; ctx.shadowBlur = rad * 0.25;
    for (var i = 0; i < 6; i++) {
      ctx.rotate(Math.PI / 3); ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -rad);
      [0.45, 0.7].forEach(function (f) { var b = rad * (0.32 - f * 0.12); ctx.moveTo(0, -rad * f); ctx.lineTo(b, -rad * f - b); ctx.moveTo(0, -rad * f); ctx.lineTo(-b, -rad * f - b); });
      ctx.stroke();
    }
    ctx.restore();
  }
  function sparkle(ctx, x, y, rad, color) {
    ctx.save(); ctx.fillStyle = color; ctx.shadowColor = color; ctx.shadowBlur = rad * 1.4; ctx.beginPath();
    ctx.moveTo(x, y - rad); ctx.quadraticCurveTo(x, y, x + rad, y); ctx.quadraticCurveTo(x, y, x, y + rad); ctx.quadraticCurveTo(x, y, x - rad, y); ctx.quadraticCurveTo(x, y, x, y - rad);
    ctx.fill(); ctx.restore();
  }
  function confetti(ctx, x, y, s, r) {
    var colors = ['#d9b54a', '#f0d98c', '#b8902a', '#f7f2ea', '#1b1a1f', '#e7c766'];
    ctx.save(); ctx.translate(x, y); ctx.rotate(r() * Math.PI); ctx.fillStyle = pick(r, colors);
    if (r() < 0.62) ctx.fillRect(-7 * s, -2.6 * s, 14 * s, 5.2 * s); else { ctx.beginPath(); ctx.arc(0, 0, 3.6 * s, 0, Math.PI * 2); ctx.fill(); }
    ctx.restore();
  }
  function streamer(ctx, x, y, len, s, r) {   // a curling ribbon: its width turns with the curl, a sheen along it
    var amp = (16 + 12 * r()) * s, ph = r() * 6, per = (20 + 8 * r()) * s, step = 1.5 * s;
    ctx.save(); ctx.lineCap = 'round'; ctx.shadowColor = 'rgba(60,40,5,0.3)'; ctx.shadowBlur = 6 * s; ctx.shadowOffsetY = 3 * s;
    var px = x, py = y;
    for (var t = step; t <= len; t += step) {
      var a = t / per + ph, nx = x + Math.sin(a) * amp * (0.35 + t / len), ny = y + t, turn = Math.abs(Math.cos(a));
      ctx.strokeStyle = turn > 0.6 ? '#f4e1a0' : turn > 0.3 ? '#d6b043' : '#9c7a1f';
      ctx.lineWidth = (2 + 7 * turn) * s; ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(nx, ny); ctx.stroke(); px = nx; py = ny;
      if (t > step * 2) ctx.shadowColor = 'transparent';
    }
    ctx.restore();
  }
  function bubble(ctx, x, y, rad) {
    ctx.save(); ctx.strokeStyle = 'rgba(255,246,214,0.95)'; ctx.lineWidth = Math.max(1.2, rad * 0.14); ctx.shadowColor = 'rgba(255,230,160,0.8)'; ctx.shadowBlur = rad * 0.8;
    ctx.fillStyle = 'rgba(255,236,180,0.22)'; ctx.beginPath(); ctx.arc(x, y, rad, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.85)'; ctx.beginPath(); ctx.arc(x - rad * 0.38, y - rad * 0.38, rad * 0.2, 0, Math.PI * 2); ctx.fill(); ctx.restore();
  }

  function drawSkin(ctx, W, H, P, key, style, part) {   // part: 'motifs', 'deco' (frame + greeting) or both
    var s = Math.max(0.5, Math.min(P.w, P.h) / 1000), F = Math.min(W, H) / 1000, top = style === 'space';
    var r = rng(key.length * 7919 + Math.round(P.w) * 31 + Math.round(P.h));
    ctx.save(); ctx.beginPath(); ctx.rect(P.x, P.y, P.w, P.h); ctx.clip();
    if (part === 'deco') { /* only the frame and greeting below */ }
    else if (key === 'moonlit') {
      vignette(ctx, P, 'rgba(26,14,38,0.62)', 0.3);
      edgeFade(ctx, P, 'rgba(26,14,38,0.45)', 0.28);
      moon(ctx, P.x + P.w * 0.83, P.y + P.h * 0.24, 66 * s);
      [[0.74, 0.1, 1.15], [0.92, 0.36, 0.95], [0.7, 0.33, 0.8], [0.87, 0.06, 0.7], [0.08, 0.1, 0.9], [0.17, 0.2, 0.7], [0.05, 0.27, 0.55]]
        .forEach(function (b) { bat(ctx, P.x + P.w * b[0], P.y + P.h * b[1], b[2] * s * 1.35, (r() - 0.5) * 0.5, 'rgba(22,13,28,0.92)'); });
    } else if (key === 'pumpkin') {
      edgeFade(ctx, P, 'rgba(70,32,8,0.38)', 0.3);
      edgeFade(ctx, P, 'rgba(40,18,6,0.35)', 0.22, true);
      var gy = P.y + P.h - 14 * s, hues = [['#ffb45a', '#e8762a', '#a8461a'], ['#ffc26e', '#ee8a33', '#b1521c'], ['#f6d79a', '#e3b462', '#a8772f']];
      pumpkin(ctx, P.x + 92 * s, gy, 78 * s, hues[0]); pumpkin(ctx, P.x + 196 * s, gy + 4 * s, 52 * s, hues[1]); pumpkin(ctx, P.x + 30 * s, gy + 6 * s, 40 * s, hues[2]);
      pumpkin(ctx, P.x + P.w - 98 * s, gy, 70 * s, hues[1]); pumpkin(ctx, P.x + P.w - 200 * s, gy + 5 * s, 44 * s, hues[0]);
      oak(ctx, P.x + 250 * s, gy - 10 * s, 0.7 * s, 0.4, '#b5502b'); maple(ctx, P.x + P.w - 260 * s, gy - 8 * s, 0.6 * s, -0.5, '#c98a2b');
    } else if (key === 'leaves') {
      edgeFade(ctx, P, 'rgba(120,60,20,0.22)', 0.25);
      var pal = ['#b5502b', '#c98a2b', '#7d2e2e', '#d9a441', '#9c4a1f', '#8a6a2a'];
      for (var i = 0; i < 30; i++) {
        var corner = r() < 0.5, p = corner ? [P.x + P.w * r() * 0.22, P.y + P.h * r() * 0.3] : [P.x + P.w * (0.78 + r() * 0.22), P.y + P.h * r() * 0.32];
        if (i > 21) p = edgePoint(r, P, 0.12);
        var sz = (0.85 + r() * 0.9) * s;
        (r() < 0.55 ? maple : oak)(ctx, p[0], p[1], sz, r() * Math.PI * 2, pick(r, pal));
      }
    } else if (key === 'harvest') {
      vignette(ctx, P, 'rgba(120,70,20,0.42)', 0.32);
      var bx = P.x + 40 * s, by = P.y + P.h - 20 * s;
      for (var k = 0; k < 14; k++) wheat(ctx, bx + k * 2.4 * s, by, (360 + r() * 110) * s, -0.2 + k * 0.075, s * 1.35);
      var bx2 = P.x + P.w - 40 * s;
      for (var k2 = 0; k2 < 12; k2++) wheat(ctx, bx2 - k2 * 2.4 * s, by, (320 + r() * 90) * s, 0.2 - k2 * 0.075, s * 1.3);
      [[bx + 18 * s, 0.3], [bx2 - 14 * s, -0.3]].forEach(function (tie) {   // a ribbon round each sheaf
        ctx.save(); ctx.translate(tie[0], by - 60 * s); ctx.rotate(tie[1]);
        ctx.fillStyle = '#8a3b1e'; roundRect(ctx, -30 * s, -9 * s, 60 * s, 18 * s, 6 * s); ctx.fill();
        ctx.beginPath(); ctx.moveTo(0, 6 * s); ctx.lineTo(-22 * s, 46 * s); ctx.lineTo(-10 * s, 44 * s); ctx.lineTo(0, 8 * s); ctx.lineTo(10 * s, 44 * s); ctx.lineTo(22 * s, 46 * s); ctx.closePath(); ctx.fill();
        ctx.restore();
      });
      maple(ctx, bx + 150 * s, by - 30 * s, 1.1 * s, 0.6, '#b5502b'); oak(ctx, bx + 95 * s, by - 12 * s, 1 * s, -0.3, '#d9a441');
      oak(ctx, bx2 - 150 * s, by - 26 * s, 1.1 * s, -0.4, '#c98a2b'); maple(ctx, bx2 - 95 * s, by - 10 * s, 0.95 * s, 0.9, '#7d2e2e');
    } else if (key === 'evergreen') {
      edgeFade(ctx, P, 'rgba(15,35,24,0.35)', 0.22);
      var x0 = P.x - 30 * s, x1 = P.x + P.w + 30 * s, yA = P.y + 26 * s, sag = Math.min(P.h * 0.08, 110 * s);
      var yAt = function (t) { return yA + sag * 4 * t * (1 - t); };
      for (var t = 0; t <= 1.0001; t += 26 * s / (x1 - x0)) {
        var gx2 = x0 + t * (x1 - x0), gyv = yAt(t);
        bough(ctx, gx2, gyv, (58 + r() * 22) * s, (r() < 0.5 ? -1 : 1) * (0.35 + r() * 0.5) + (r() < 0.5 ? 0 : Math.PI), s, r);
      }
      for (var b = 0.06; b < 1; b += 0.11) berries(ctx, x0 + b * (x1 - x0), yAt(b) + 6 * s, s, r);
      [0.12, 0.3, 0.7, 0.88].forEach(function (u, i) { bauble(ctx, x0 + u * (x1 - x0), yAt(u) + 10 * s, (i % 3 ? 22 : 27) * s, [44, 70, 58, 36][i] * s, s); });
    } else if (key === 'snow') {
      edgeFade(ctx, P, 'rgba(235,243,250,0.55)', 0.24); edgeFade(ctx, P, 'rgba(235,243,250,0.45)', 0.2, true);
      vignette(ctx, P, 'rgba(180,205,225,0.32)', 0.4);
      for (var f = 0; f < 170; f++) {
        var fx = P.x + r() * P.w, fy = P.y + r() * P.h, fr = (1.2 + r() * r() * 5) * s;
        ctx.save(); ctx.fillStyle = 'rgba(255,255,255,' + (0.55 + r() * 0.4) + ')'; if (fr > 3.5 * s) { ctx.shadowColor = 'rgba(255,255,255,0.9)'; ctx.shadowBlur = fr * 2; }
        ctx.beginPath(); ctx.arc(fx, fy, fr, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      }
      [[0.08, 0.1, 54], [0.92, 0.14, 44], [0.06, 0.84, 40], [0.94, 0.8, 58], [0.82, 0.06, 26], [0.18, 0.05, 22]].forEach(function (q) { snowflake(ctx, P.x + P.w * q[0], P.y + P.h * q[1], q[2] * s, r(), 0.95); });
    } else if (key === 'midnight') {
      vignette(ctx, P, 'rgba(8,10,22,0.62)', 0.28);
      edgeFade(ctx, P, 'rgba(8,10,22,0.45)', 0.25);
      for (var c = 0; c < 110; c++) { var cp = edgePoint(r, P, 0.17); confetti(ctx, cp[0], cp[1], (0.8 + r() * 0.8) * s, r); }
      for (var k3 = 0; k3 < 12; k3++) { var sp = edgePoint(r, P, 0.15); sparkle(ctx, sp[0], sp[1], (8 + r() * 16) * s, r() < 0.5 ? '#f6dd8a' : '#fff4cf'); }
    } else if (key === 'champagne') {
      edgeFade(ctx, P, 'rgba(60,45,15,0.3)', 0.3);
      vignette(ctx, P, 'rgba(70,50,12,0.32)', 0.4);
      [0.04, 0.12, 0.21, 0.79, 0.88, 0.96].forEach(function (u, i) { streamer(ctx, P.x + P.w * u, P.y - 10 * s, P.h * (0.2 + [0.08, 0.16, 0, 0.12, 0.2, 0.04][i] + r() * 0.05), s, r); });
      for (var q2 = 0; q2 < 70; q2++) {
        var side = r() < 0.5, bxp = side ? P.x + r() * P.w * 0.16 : P.x + P.w * (0.84 + r() * 0.16);
        bubble(ctx, bxp, P.y + P.h * (0.3 + r() * 0.7), (4 + r() * r() * 16) * s);
      }
      for (var k4 = 0; k4 < 8; k4++) { var sp2 = edgePoint(r, P, 0.14); sparkle(ctx, sp2[0], sp2[1], (7 + r() * 12) * s, '#f6dd8a'); }
    }
    ctx.restore();
    // the frame and the greeting
    var yr = newYear();
    var G = {
      moonlit: [['#d9772b', '#3a2440'], 'Happy Halloween', { bg: '#1f1524', fg: '#f2b35c', ring: '#d9772b' }],
      pumpkin: [['#1f1524', '#e07a26'], 'Trick or treat', { bg: '#e07a26', fg: '#1f1524', ring: 'rgba(31,21,36,0.55)' }],
      leaves: [['#b5502b', '#d9a441'], 'Happy Thanksgiving', { bg: '#f7f2ea', fg: '#8a3b1e', ring: '#c98a2b' }],
      harvest: [['#8a3b1e', '#c98a2b'], 'With gratitude', { bg: '#8a3b1e', fg: '#f7e7c6', ring: '#d9a441' }],
      evergreen: [['#c9a227', '#1f3b2c'], 'Happy holidays', { bg: '#1f3b2c', fg: '#f3e3b5', ring: '#c9a227' }],
      snow: [['#a9c3d6', '#ffffff'], 'Season’s greetings', { bg: 'rgba(255,255,255,0.9)', fg: '#24384f', ring: '#a9c3d6' }],
      midnight: [['#c9a227', '#e7cf86'], 'Happy New Year ' + yr, { bg: '#121218', fg: '#e2c56b', ring: '#c9a227' }],
      champagne: [['#c9a227', '#f0dc9a'], 'Cheers to ' + yr, { bg: 'rgba(247,242,234,0.94)', fg: '#6b5320', ring: '#c9a227' }]
    }[key];
    if (!G || part === 'motifs') return;
    frame(ctx, W, H, G[0], F);
    greeting(ctx, P, G[1], G[2], top ? P.y + Math.round(285 * F) : false);
  }
  NS.skinGreeting = function (key) { return { moonlit: 'Happy Halloween', pumpkin: 'Happy Halloween', leaves: 'Happy Thanksgiving', harvest: 'Happy Thanksgiving', evergreen: 'Happy holidays', snow: 'Season’s greetings', midnight: 'Happy New Year', champagne: 'Happy New Year' }[key] || ''; };

  // ---------------------------------------------------------------- the picture
  function draw(ctx, W, H, spec) {
    var t = spec.t, ims = spec.photos, p1 = ims[0], p2 = ims[1] || ims[0], sw = spec.swatch, py = spec.py == null ? 0.5 : spec.py;
    var land = W / H > 1.3, photo = { x: 0, y: 0, w: W, h: H };
    ctx.save(); ctx.clearRect(0, 0, W, H); ctx.textBaseline = 'alphabetic';
    var st = spec.style;
    if (st === 'plain') {
      cover(ctx, p1, 0, 0, W, H, 0.5, py);
    } else if (st === 'band' || st === 'dims' || st === 'finish') {
      ctx.fillStyle = CREAM; ctx.fillRect(0, 0, W, H);
      var content = st === 'band' ? bandA(t, sw) : st === 'dims' ? bandDims(t, sw) : bandFinish(t, sw);
      if (land) {
        photo = { x: 0, y: 0, w: Math.round(W * 0.56), h: H };
        cover(ctx, p1, photo.x, photo.y, photo.w, photo.h, 0.5, py);
        inBox(ctx, photo.w + W * 0.02, H * 0.04, W - photo.w - W * 0.04, H * 0.92, 1000, 753, content);
      } else {
        var bf = { pin: 0.502, post: 0.43, story: 0.4, square: 0.44 }[spec.format] || 0.45;
        var bh = Math.round(H * bf);
        photo = { x: 0, y: 0, w: W, h: H - bh };
        cover(ctx, p1, 0, 0, W, H - bh, 0.5, py);
        inBox(ctx, 0, H - bh, W, bh, 1000, 753, content);
      }
    } else if (st === 'two') {
      ctx.fillStyle = CREAM; ctx.fillRect(0, 0, W, H);
      if (land) {
        photo = { x: 0, y: 0, w: Math.round(W * 0.55), h: H };
        cover(ctx, p1, 0, 0, photo.w, H, 0.5, py);
        var rx = photo.w + 8, rw = W - rx, sh = Math.round(H * 0.56);
        cover(ctx, p2, rx, 0, rw, sh, 0.5, 0.55);
        inBox(ctx, rx, sh, rw, H - sh, 1000, 325, bandB(t, sw));
      } else {
        var f2 = { pin: [0.498, 0.28], post: [0.5, 0.25], story: [0.5, 0.3], square: [0.52, 0.22] }[spec.format] || [0.5, 0.26];
        var h1 = Math.round(H * f2[0]), h2 = Math.round(H * f2[1]);
        photo = { x: 0, y: 0, w: W, h: h1 };
        cover(ctx, p1, 0, 0, W, h1, 0.5, py);
        cover(ctx, p2, 0, h1 + 8, W, h2, 0.5, 0.55);
        inBox(ctx, 0, h1 + 8 + h2, W, H - h1 - 8 - h2, 1000, 325, bandB(t, sw));
      }
    } else if (st === 'frame') {
      ctx.fillStyle = '#211b17'; ctx.fillRect(0, 0, W, H);
      ctx.save(); try { ctx.filter = 'blur(' + Math.round(Math.max(W, H) * 0.025) + 'px) brightness(0.55) saturate(0.9)'; } catch (e) {}
      cover(ctx, p1, -W * 0.075, -H * 0.075, W * 1.15, H * 1.15); ctx.restore();
      ctx.fillStyle = 'rgba(20,15,12,0.25)'; ctx.fillRect(0, 0, W, H);
      if (land) {
        var m = Math.round(H * 0.07);
        photo = { x: m, y: m, w: Math.round(W * 0.58) - m, h: H - 2 * m };
        ctx.save(); ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 40; ctx.shadowOffsetY = 14;
        cover(ctx, p1, photo.x, photo.y, photo.w, photo.h, 0.5, py); ctx.restore();
        inBox(ctx, photo.x + photo.w + W * 0.02, H * 0.04, W - photo.x - photo.w - W * 0.04, H * 0.92, 1000, 753, bandA(t, sw, true));
      } else {
        var k = Math.min(W / 1000, (H * 0.55) / 753), top = Math.round(250 * k), txt = Math.round(503 * k);
        wordmark(ctx, Math.round(104 * k), W / 2, Math.round(160 * k), 'center', CREAM);
        photo = { x: 0, y: top, w: W, h: H - top - txt };
        ctx.save(); ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 50 * k; ctx.shadowOffsetY = 18 * k;
        cover(ctx, p1, 0, top, W, photo.h, 0.5, py); ctx.restore();
        ctx.save(); ctx.translate(0, top + photo.h); ctx.scale(k, k);
        var tw = W / k - 140;
        ctx.textAlign = 'left'; ctx.fillStyle = CREAM; fit(ctx, t.name, 56, tw, { serif: true, track: -0.01 }); ctx.fillText(t.name, 70, 241);
        ctx.fillStyle = '#e6ddd1'; fit(ctx, t.size, 26, tw, { track: 0.02 }); ctx.fillText(t.size, 70, 297);
        chip(ctx, sw, t.finish, 70, 355, 'left', CREAM, CREAM);
        ctx.fillStyle = '#d9cfc2'; font(ctx, 21); lines(ctx, t.label, 70, 407, Math.min(760, tw), 29, 3);
        ctx.restore();
      }
    } else if (st === 'space') {
      cover(ctx, p1, 0, 0, W, H, 0.5, py);
      if (spec.skin && spec.skin !== 'none') drawSkin(ctx, W, H, { x: 0, y: 0, w: W, h: H }, spec.skin, st, 'motifs');
      var g = ctx.createLinearGradient(0, H * 0.35, 0, H);
      g.addColorStop(0, 'rgba(25,19,15,0)'); g.addColorStop(0.55, 'rgba(25,19,15,0.62)'); g.addColorStop(1, 'rgba(25,19,15,0.86)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      var gt = ctx.createLinearGradient(0, 0, 0, H * 0.22); gt.addColorStop(0, 'rgba(25,19,15,0.45)'); gt.addColorStop(1, 'rgba(25,19,15,0)');
      ctx.fillStyle = gt; ctx.fillRect(0, 0, W, H * 0.22);
      var s = Math.min(W, H) / 1000;
      wordmark(ctx, Math.round(64 * s), Math.round(52 * s), Math.round(100 * s), 'left', CREAM);
      inBox(ctx, 0, land ? H * 0.38 : H * 0.5, W, land ? H * 0.6 : H * 0.48, 1000, 560, function (c) {
        arIcon(c, 500, 70, 96, CREAM);
        c.textAlign = 'center'; c.fillStyle = CREAM; fit(c, 'See it in your space', 84, 920, { serif: true, track: -0.02 }); c.fillText('See it in your space', 500, 215);
        c.fillStyle = '#efe7dc'; font(c, 28); lines(c, 'Tap View in your space on nookandwave.com to place it in your room at its true size.', 500, 278, 820, 38, 2);
        c.fillStyle = CREAM; fit(c, t.name, 40, 900, { serif: true, track: -0.01 }); c.fillText(t.name, 500, 400);
        chip(c, sw, t.finish, 500, 458, 'center', CREAM, CREAM);
        c.textAlign = 'center'; c.fillStyle = '#d9cfc2'; font(c, 20); fit(c, t.label, 20, 940); c.fillText(t.label, 500, 530);
      }, Math.max(W, H) / 1000);
    }
    // the season's skin over the template, under the tags
    if (spec.skin && spec.skin !== 'none') drawSkin(ctx, W, H, photo, spec.skin, st, st === 'space' ? 'deco' : null);
    // the light tags, laid on the photo's top corners
    var ts = Math.max(0.55, Math.min(photo.w, photo.h, 1000) / 1000) * (Math.min(W, H) / 1000 > 0.9 ? 1 : 0.9);
    var pad = Math.round(26 * ts) + (spec.skin && spec.skin !== 'none' ? Math.round(18 * ts) : 0);
    var topY = photo.y + pad + (st === 'space' ? Math.round(120 * Math.min(W, H) / 1000) : 0);
    if (spec.tags && spec.tags.swatch) {
      ctx.save(); font(ctx, Math.round(26 * ts), { weight: 500 }); var fw = ctx.measureText(t.finish).width;
      var th = Math.round(62 * ts), tw2 = Math.round(th + 10 * ts + fw + 22 * ts), x0 = photo.x + pad;
      ctx.shadowColor = 'rgba(0,0,0,0.18)'; ctx.shadowBlur = 16 * ts; roundRect(ctx, x0, topY, tw2, th, th / 2); ctx.fillStyle = 'rgba(247,242,234,0.94)'; ctx.fill(); ctx.restore();
      swatchDot(ctx, sw, t.finish, x0 + th / 2, topY + th / 2, th / 2 - 7 * ts);
      ctx.fillStyle = INK; ctx.textAlign = 'left'; font(ctx, Math.round(26 * ts), { weight: 500 }); ctx.fillText(t.finish, x0 + th + 4 * ts, topY + th / 2 + 9 * ts);
    }
    if (spec.tags && spec.tags.ar) {
      ctx.save(); font(ctx, Math.round(25 * ts), { weight: 500 }); var lw = ctx.measureText('View in your space').width;
      var ah = Math.round(62 * ts), aw = Math.round(ah + lw + 22 * ts), ax = photo.x + photo.w - pad - aw;
      ctx.shadowColor = 'rgba(0,0,0,0.25)'; ctx.shadowBlur = 16 * ts; roundRect(ctx, ax, topY, aw, ah, ah / 2); ctx.fillStyle = 'rgba(46,38,33,0.88)'; ctx.fill(); ctx.restore();
      arIcon(ctx, ax + ah / 2 + 4 * ts, topY + ah / 2, ah * 0.52, CREAM);
      ctx.fillStyle = CREAM; ctx.textAlign = 'left'; font(ctx, Math.round(25 * ts), { weight: 500 }); ctx.fillText('View in your space', ax + ah + 2 * ts, topY + ah / 2 + 9 * ts);
    }
    if (spec.tags && spec.tags.partner && spec.ref) {   // the partner's own link, readable where links don't travel
      var pl = 'nookandwave.com/?ref=' + spec.ref;
      ctx.save(); font(ctx, Math.round(22 * ts), { weight: 500 }); var pw = ctx.measureText(pl).width;
      var ph2 = Math.round(48 * ts), pw2 = Math.round(pw + 40 * ts), px = photo.x + photo.w - pad - pw2, py2 = photo.y + photo.h - pad - ph2;
      ctx.shadowColor = 'rgba(0,0,0,0.18)'; ctx.shadowBlur = 14 * ts; roundRect(ctx, px, py2, pw2, ph2, ph2 / 2); ctx.fillStyle = 'rgba(247,242,234,0.94)'; ctx.fill(); ctx.restore();
      ctx.fillStyle = INK; ctx.textAlign = 'left'; font(ctx, Math.round(22 * ts), { weight: 500 }); ctx.fillText(pl, px + 20 * ts, py2 + ph2 / 2 + 8 * ts);
    }
    ctx.restore();
  }

  // ---------------------------------------------------------------- the words for a page and a selection
  function fmt(n) { return String(n || '').trim(); }
  function sizeOf(D) {
    var o = D.dims || {};
    if (o.w) return { w: o.w, h: o.h, d: o.d, cm: o.cm || '' };
    var m = String(D.sizeText || '').match(/([\d\/ ]+?)\s*W\s*x\s*([\d\/ ]+?)\s*H\s*x\s*([\d\/ ]+?)\s*D\s*in(?:\s*\(([^)]+)\))?/i);
    return m ? { w: fmt(m[1]), h: fmt(m[2]), d: fmt(m[3]), cm: m[4] ? m[4].replace(/\s*x\s*/g, ' × ') : '' } : null;
  }
  function wordsFor(D, fin) {
    var parts = String(D.title || '').split(' – ');
    var name = parts.length > 2 ? parts.slice(0, -1).join(' – ') : parts[0];
    var shortT = parts.length > 2 ? parts[parts.length - 1] : '';
    var cubesShort = shortT.replace(/^(\d+)x(\d+) Cube$/, '$1 x $2 cubes');
    var d = sizeOf(D), sizeIn = d ? d.w + ' × ' + d.h + ' × ' + d.d + ' in' : '';
    var size = [/cubes$/.test(cubesShort) ? cubesShort : '', sizeIn].filter(Boolean).join(' · ') || shortT;
    var fname = String(fin.name || '').replace(' with natural maple edge', '');
    return {
      name: name, short: cubesShort || sizeIn, size: size, finish: fname, fullFinish: fin.name,
      label: D.label, dims: d ? { w: d.w, h: d.h, d: d.d, cm: d.cm ? d.cm + (/cm/.test(d.cm) ? '' : ' cm') : '' } : null,
      cubes: D.cubesLine || '', code: CODE[fin.name] || CODE[fname] || '', words: WORDS[fin.name] || WORDS[fname] || ''
    };
  }
  NS.wordsFor = wordsFor;

  NS.buildSpec = function (D, sel) {
    var fin = D.finishes[sel.finish] || D.finishes[0], ph = fin.photos[sel.photo] || fin.photos[0];
    var second = fin.photos[(sel.photo + 1) % fin.photos.length];
    if (!second || second === ph) { var nf = D.finishes[(sel.finish + 1) % D.finishes.length]; second = nf && nf.photos[0]; }
    return {
      format: sel.format, style: sel.style, skin: sel.skin || 'none', py: ph.py == null ? 0.5 : ph.py,
      photoUrls: [ph.url, second && second.url], swatchUrl: fin.swatch || '',
      t: wordsFor(D, fin), tags: { swatch: !!(sel.tags && sel.tags.swatch), ar: !!(sel.tags && sel.tags.ar && D.ar), partner: !!(sel.tags && sel.tags.partner && sel.ref) }, ref: sel.ref || ''
    };
  };
  NS.render = function (canvas, spec, fonts) {
    var f = FORMATS.filter(function (x) { return x.key === spec.format; })[0] || FORMATS[0];
    canvas.width = f.w; canvas.height = f.h;
    var need = [spec.photoUrls[0], spec.style === 'two' ? spec.photoUrls[1] : null, (spec.tags.swatch || spec.style !== 'plain') ? spec.swatchUrl : null];
    return Promise.all([NS.loadFonts(fonts)].concat(need.map(loadImage))).then(function (r) {
      draw(canvas.getContext('2d'), f.w, f.h, { format: spec.format, style: spec.style, skin: spec.skin, py: spec.py, photos: [r[1], r[2] || r[1]], swatch: r[3], t: spec.t, tags: spec.tags, ref: spec.ref });
      return canvas;
    });
  };

  function story(D) {
    var s = String(D.story || '').replace(/\s+/g, ' ').trim();
    if (s.length > 320) { var cut = s.slice(0, 320), i = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! ')); s = i > 120 ? cut.slice(0, i + 1) : cut.replace(/\s+\S*$/, '') + '…'; }
    return s;
  }
  function hashtags(D, skin) {
    var n = String(D.title || '').toLowerCase(), t = ['#nookandwave', '#madeinmichigan', '#sculpturalfurniture', '#modernfurniture', '#interiordesign'];
    [[/bookshelf|bookcase|library/, '#bookshelf #bookshelfstyling'], [/record/, '#vinylcollection #recordstorage'], [/tv stand|media/, '#tvstand #mediaconsole'],
     [/shelf/, '#shelfstyling'], [/dresser|credenza/, '#bedroomdecor'], [/bench|hall/, '#entrywaydecor'], [/bar /, '#homebar'], [/wine/, '#winestorage'],
     [/pantry/, '#pantrygoals'], [/office/, '#homeoffice'], [/plant/, '#plantshelf'], [/divider/, '#roomdivider'], [/wavy|wave/, '#wavyfurniture']]
      .forEach(function (r) { if (r[0].test(n)) t.push(r[1]); });
    var sk = SKINS.filter(function (x) { return x.key === skin; })[0];
    var season = sk && sk.season;
    if (season) t.push({ halloween: '#halloweendecor #spookyseason', thanksgiving: '#thanksgivingdecor #fallhome', holidays: '#holidaydecor #christmasdecor', newyear: '#newyear #newyearnewroom' }[season]);
    return t.join(' ');
  }
  NS.caption = function (D, sel, key) {
    var fin = D.finishes[sel.finish] || D.finishes[0], w = wordsFor(D, fin), ai = 'Pictured: a rendering of its exact design, in a room staged with AI.';
    var made = D.kind === 'special' ? 'A special order design, made to order in Michigan.'
      : D.kind === 'custom' ? 'Like it in your own size? Nook & Wave designs and builds custom pieces to your wall and finish, made in Michigan.'   // the Partner Studio's Custom posts
      : 'Sculptural furniture, made to order in Michigan.';
    var full = w.name + (w.short ? ' (' + w.short + ')' : '');
    var finishes = D.finishes.map(function (f) { return String(f.name).replace(' with natural maple edge', ' with a natural maple edge'); });
    var list = finishes.length > 1 ? finishes.slice(0, -1).join(', ') + ' and ' + finishes[finishes.length - 1] : finishes[0];
    var d = w.dims, out;
    if (key === 'none') return '';
    if (key === 'story') out = (story(D) || full + ' in ' + w.finish + '.') + ' Shown in ' + w.finish + '.';
    else if (key === 'dims') out = full + ': about ' + (d ? d.w + ' W × ' + d.h + ' H × ' + d.d + ' D in' + (d.cm ? ' (' + d.cm + ')' : '') : '') + '.' + (w.cubes ? ' ' + w.cubes + '.' : '') + ' Shown in ' + w.finish + '. ' + made;
    else if (key === 'finish') out = full + ', shown in ' + w.finish + (w.words ? ': ' + w.words : '') + '. Made in ' + list + '.';
    else out = full + ' in ' + w.finish + '. ' + made;
    var greet = NS.skinGreeting(sel.skin);
    if (greet) out = greet + ' from Nook & Wave. ' + out;
    out += ' ' + ai;
    if (key === 'tags') out += '\n\n' + hashtags(D, sel.skin);
    return out;
  };

  // ---------------------------------------------------------------- the partner (affiliate) code
  // ONLY the code a partner typed into the composer on this device (local storage, until they remove it). GoAffPro's
  // own `ref` cookie is never read here: a visitor who came by a partner's link must not carry that partner's code
  // into their own shares. No lookup (that needs GoAffPro's public token in the page, which lists every partner):
  // the code is shown back so a typo shows, and GoAffPro ignores a code it does not know.
  var PKEY = 'nw_partner_code';
  function cleanRef(r) { r = String(r || '').trim().replace(/^.*[?&]ref=/, '').replace(/[&#].*$/, ''); return /^[A-Za-z0-9_-]{1,40}$/.test(r) ? r : ''; }
  function partnerRef() { try { return cleanRef(localStorage.getItem(PKEY)); } catch (e) { return ''; } }
  function setPartnerRef(r) { try { if (r) localStorage.setItem(PKEY, r); else localStorage.removeItem(PKEY); } catch (e) {} }
  NS.partnerRef = partnerRef; NS.cleanRef = cleanRef;

  // ---------------------------------------------------------------- the link and the trace
  function shareId() { var r = ''; var a = 'abcdefghijklmnopqrstuvwxyz0123456789'; for (var i = 0; i < 4; i++) r += a[Math.floor(Math.random() * 36)]; return Date.now().toString(36) + '-' + r; }
  function slug(s) { return String(s || '').toLowerCase().replace(/ with natural maple edge/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }
  NS.link = function (D, sel, source, id, deck) {
    var fin = D.finishes[sel.finish] || D.finishes[0];
    var u = new URL(D.url);
    if (fin.variant) u.searchParams.set('variant', fin.variant);
    if (fin.n) u.searchParams.set('finish', fin.n);
    u.searchParams.set('utm_source', source);
    u.searchParams.set('utm_medium', 'social_share');
    u.searchParams.set('utm_campaign', D.handle);
    u.searchParams.set('utm_content', (deck ? ['deck' + deck, sel.format] : [sel.style, slug(fin.name), sel.format].concat(sel.skin && sel.skin !== 'none' ? [sel.skin] : [])).join('-'));
    u.searchParams.set('utm_id', id);
    if (sel.ref) u.searchParams.set('ref', sel.ref);
    return u.toString();
  };
  function track(D, sel, platform, id, captionKey, deck) {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function' && !cp.analyticsProcessingAllowed()) return;
      var c = document.cookie, m = c.match(/(?:^|; )_ga=GA\d\.\d\.(\d+\.\d+)/);
      if (!m) return;
      var s = c.match(new RegExp('(?:^|; )_ga_' + GA.slice(2) + '=([^;]+)')), sid = '';
      if (s) { var v = decodeURIComponent(s[1]), mm = v.match(/(?:^|[.$])s(\d+)/) || v.match(/^GS\d\.\d\.(\d+)/); sid = mm ? mm[1] : ''; }
      var fin = D.finishes[sel.finish] || D.finishes[0], ph = fin.photos[sel.photo] || fin.photos[0];
      var p = new URLSearchParams({ v: '2', tid: GA, cid: m[1], en: 'nw_share', dl: location.href.split('#')[0], dt: document.title, _et: '1', seg: '1' });
      if (sid) p.set('sid', sid);
      var ep = { share_id: id, share_platform: platform, share_style: sel.style, share_format: sel.format, share_finish: fin.name,
        share_photo: String(ph.key || ''), share_tags: [sel.tags.swatch ? 'swatch' : '', sel.tags.ar ? 'ar' : '', sel.tags.partner ? 'partner' : ''].filter(Boolean).join(',') || 'none',
        share_skin: sel.skin || 'none', share_caption: captionKey, share_page: D.handle, share_kind: D.kind, share_deck: String(deck || 0), share_ref: sel.ref || 'none' };
      Object.keys(ep).forEach(function (k) { p.set('ep.' + k, ep[k]); });
      var url = 'https://www.google-analytics.com/g/collect?' + p.toString();
      if (navigator.sendBeacon) navigator.sendBeacon(url); else fetch(url, { method: 'POST', mode: 'no-cors', keepalive: true });
    } catch (e) { /* a share never fails for the trace */ }
  }

  // ---------------------------------------------------------------- the composer
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
  NS.mount = function (root) {
    if (!root || root.__nwSoc) return;
    root.__nwSoc = true;
    var D; try { D = JSON.parse(root.querySelector('script[type="application/json"]').textContent); } catch (e) { return; }
    if (!D.finishes || !D.finishes.length) return;
    if (D.kind === 'special') { var qf = (location.search.match(/[?&]finish=(\d)/) || [])[1]; if (qf) D.finishes.forEach(function (f, i) { if (String(f.n) === qf) D.start = i; }); }
    var st = { finish: Math.max(0, D.start || 0), photo: 0, format: 'pin', style: 'band', skin: 'none', tags: { swatch: false, ar: false, partner: false }, cap: 'short', edited: false, ref: partnerRef() };
    var deck = [], editing = -1;   // [{ sel, blob, thumb }], one size; editing: the card loaded back to change

    var styles = STYLES.filter(function (s) { return !s.ar || D.ar; });
    var skinChips = SKINS.map(function (k) { return '<button type="button" data-sskin="' + k.key + '">' + (k.season ? '<span>' + esc(SEASON_NAME[k.season]) + '</span> ' : '') + esc(k.name) + '</button>'; }).join('');
    root.insertAdjacentHTML('beforeend',
      '<div class="nw-soc__grid">' +
        '<div class="nw-soc__preview"><canvas class="nw-soc__canvas" aria-label="The picture to share"></canvas><p class="nw-soc__size"></p></div>' +
        '<div class="nw-soc__controls">' +
          '<p class="nw-soc__partner" hidden></p>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Finish</p><div class="nw-soc__chips nw-soc__finishes"></div></div>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Photo</p><div class="nw-soc__thumbs"></div></div>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Size</p><div class="nw-soc__chips">' + FORMATS.map(function (f) { return '<button type="button" data-sfmt="' + f.key + '" title="' + esc(f.note) + '">' + esc(f.name) + ' <span>' + f.ratio + '</span></button>'; }).join('') + '</div></div>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Style</p><div class="nw-soc__chips">' + styles.map(function (s) { return '<button type="button" data-sst="' + s.key + '">' + esc(s.name) + '</button>'; }).join('') + '</div></div>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Season</p><div class="nw-soc__chips">' + skinChips + '</div></div>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Add a tag</p><div class="nw-soc__chips"><button type="button" data-stag="swatch">Finish swatch</button>' + (D.ar ? '<button type="button" data-stag="ar">View in your space</button>' : '') + '<button type="button" data-stag="partner" class="nw-soc__partnertag" hidden>Partner link on the picture</button></div></div>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Caption</p><div class="nw-soc__chips">' + CAPTIONS.map(function (c) { return '<button type="button" data-scap="' + c.key + '">' + esc(c.name) + '</button>'; }).join('') + '</div>' +
            '<textarea class="nw-soc__text" rows="4" aria-label="Caption"></textarea></div>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Share to</p><div class="nw-soc__go">' +
            '<button type="button" data-sgo="pinterest">Pinterest</button>' +
            '<button type="button" data-sgo="instagram">Instagram</button>' +
            '<button type="button" data-sgo="facebook">Facebook</button>' +
            '<button type="button" data-sgo="more">Share…</button>' +
            '<button type="button" data-sgo="download">Download</button>' +
            '<button type="button" data-sgo="copy_link">Copy link</button>' +
          '</div><p class="nw-soc__status" role="status" aria-live="polite"></p></div>' +
          '<div class="nw-soc__group"><p class="nw-soc__label">Deck <span class="nw-soc__decknote">several pictures in one post: an Instagram carousel</span></p>' +
            '<div class="nw-soc__deck"></div><div class="nw-soc__go"><button type="button" data-sgo="deck_add">Add this picture</button><button type="button" data-sgo="deck_share" disabled>Share the deck</button><button type="button" data-sgo="deck_clear" disabled>Clear</button></div></div>' +
          '<p class="nw-soc__fine">Pictures are renderings of the exact design, in rooms staged with AI. The share link tells us which share brought a visit; it carries nothing about you.</p>' +
        '</div>' +
      '</div>');
    var q = function (s) { return root.querySelector(s); }, qa = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };
    var canvas = q('.nw-soc__canvas'), text = q('.nw-soc__text'), status = q('.nw-soc__status');
    var blob = null, token = 0;
    // can this browser hand picture FILES to the device's share sheet? (phones; Chrome / Edge on Windows; Safari on a Mac)
    var canFiles = false;
    try { canFiles = !!(navigator.share && navigator.canShare && navigator.canShare({ files: [new File([new Blob(['x'], { type: 'image/jpeg' })], 'x.jpg', { type: 'image/jpeg' })] })); } catch (e) {}

    function say(msg) { status.textContent = msg || ''; }
    function finishChips() {
      q('.nw-soc__finishes').innerHTML = D.finishes.map(function (f, i) {
        return '<button type="button" data-sf="' + i + '"><span class="nw-soc__dot" style="' + (f.swatch ? 'background-image:url(' + esc(f.swatch) + ')' : 'background:' + esc(f.colour || DOT[f.name] || '#a99')) + '"></span>' + esc(String(f.name).replace(' with natural maple edge', '')) + '</button>';
      }).join('');
    }
    function thumbs() {
      var f = D.finishes[st.finish];
      q('.nw-soc__thumbs').innerHTML = f.photos.map(function (p, i) {
        return '<button type="button" data-sp="' + i + '" title="' + esc(p.view || '') + '"><img src="' + esc(p.thumb || p.url) + '" alt="' + esc(p.view || 'Photo ' + (i + 1)) + '" loading="lazy" width="96" height="72"></button>';
      }).join('');
    }
    function marks() {
      qa('[data-sf]').forEach(function (b) { b.setAttribute('aria-pressed', String(+b.dataset.sf === st.finish)); });
      qa('[data-sp]').forEach(function (b) { b.setAttribute('aria-pressed', String(+b.dataset.sp === st.photo)); });
      qa('[data-sfmt]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.sfmt === st.format)); });
      qa('[data-sst]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.sst === st.style)); });
      qa('[data-sskin]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.sskin === st.skin)); });
      qa('[data-stag]').forEach(function (b) { b.setAttribute('aria-pressed', String(!!st.tags[b.dataset.stag])); });
      qa('[data-scap]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.scap === st.cap)); });
      var f = FORMATS.filter(function (x) { return x.key === st.format; })[0];
      q('.nw-soc__size').textContent = f.note;
      canvas.style.aspectRatio = f.w + ' / ' + f.h;
    }
    function partnerLine(editing) {
      var p = q('.nw-soc__partner'), chip = q('.nw-soc__partnertag');
      chip.hidden = !st.ref; if (!st.ref) st.tags.partner = false;
      p.hidden = false;
      if (editing) p.innerHTML = '<label>Your partner code <input type="text" class="nw-soc__pcode" value="' + esc(st.ref) + '" placeholder="e.g. sarah" autocomplete="off" autocapitalize="off" spellcheck="false" maxlength="60"></label> <button type="button" data-spsave="1">Save</button>' + (st.ref ? ' <button type="button" data-spcancel="1">Cancel</button>' : '') + '<br><span>It is in your partner dashboard (your link ends in ?ref=your-code). Your device should remember it.</span>';
      else if (st.ref) p.innerHTML = 'Your partner code: <strong>' + esc(st.ref) + '</strong>. Your shares carry it. <button type="button" data-spedit="1">Change</button> <button type="button" data-spclear="1">Remove</button>';
      else p.innerHTML = '<button type="button" data-spedit="1">Partner? Enter your code</button>';
    }
    function caption(force) { if (force || !st.edited) { text.value = NS.caption(D, st, st.cap); st.edited = false; } }
    function snapshot() { return { finish: st.finish, photo: st.photo, format: st.format, style: st.style, skin: st.skin, ref: st.ref, tags: { swatch: st.tags.swatch, ar: st.tags.ar, partner: st.tags.partner } }; }
    function toBlob(cv) { return new Promise(function (ok) { cv.toBlob(function (b) { ok(b); }, 'image/jpeg', 0.92); }); }
    function draw() {
      var my = ++token; marks(); blob = null;
      NS.render(canvas, NS.buildSpec(D, st), D.fonts).then(function () { return toBlob(canvas); }).then(function (b) { if (my === token) blob = b; });
    }
    function fileName(sel, n) {
      var fin = D.finishes[sel.finish];
      return ['nook-and-wave', D.handle, slug(fin.name), sel.style].concat(sel.skin && sel.skin !== 'none' ? [sel.skin] : []).concat([sel.format]).concat(n ? [String(n)] : []).join('-') + '.jpg';
    }
    function copy(s) { if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(s).catch(function () {}); return Promise.resolve(); }
    function save(b, name) {
      var a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = name;
      document.body.appendChild(a); a.click(); setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 4000);
    }
    function captionWith(link) { var c = text.value.trim(); return c ? c + '\n\n' + link : link; }
    var NAME = { pinterest: 'Pinterest', instagram: 'Instagram', facebook: 'Facebook', share_sheet: 'the app' };

    // one picture: the share sheet with the file, else download it and copy the caption and link
    function sendOne(platform) {
      if (!blob) { say('One moment, the picture is still being drawn.'); return; }
      var id = shareId(), link = NS.link(D, st, platform, id), cap = captionWith(link);
      if (canFiles) {
        copy(cap);
        navigator.share({ files: [new File([blob], fileName(st), { type: 'image/jpeg' })], text: cap })
          .then(function () { track(D, st, platform, id, st.cap); say('Shared. The caption and link were also copied, in case ' + NAME[platform] + ' leaves them out.'); })
          .catch(function (e) { if (e && e.name !== 'AbortError') fallback(platform, id, link, cap); });
      } else fallback(platform, id, link, cap);
    }
    function fallback(platform, id, link, cap) {
      if (platform === 'facebook') {
        copy(text.value.trim());
        window.open('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(link), '_blank', 'noopener,noreferrer,width=680,height=640');
        track(D, st, platform, id, st.cap); say('Opened Facebook with the link (its preview shows the listing’s first photo). The caption was copied: paste it in.');
        return;
      }
      save(blob, fileName(st)); copy(cap); track(D, st, platform === 'share_sheet' ? 'download' : platform, id, st.cap);
      say(platform === 'instagram' ? 'This browser can’t hand a picture to Instagram, so it was downloaded and the caption and link copied: post it from the Instagram app.'
        : platform === 'pinterest' ? 'This browser can’t hand a picture to Pinterest, so it was downloaded and the caption and link copied: add it at pinterest.com.'
        : 'Downloaded. The caption and its link were copied.');
    }
    var GO = {
      pinterest: function () {
        var tagged = st.tags.swatch || (st.tags.ar && D.ar) || (st.tags.partner && st.ref);
        if (st.style === 'plain' && !tagged && st.skin === 'none') {   // the listing photo itself: Pinterest's own Save window
          var fin = D.finishes[st.finish], ph = fin.photos[st.photo] || fin.photos[0], id = shareId(), link = NS.link(D, st, 'pinterest', id);
          window.open('https://www.pinterest.com/pin/create/button/?url=' + encodeURIComponent(link) + '&media=' + encodeURIComponent(ph.url) + '&description=' + encodeURIComponent(text.value.trim().slice(0, 500)), '_blank', 'noopener,noreferrer,width=760,height=680');
          track(D, st, 'pinterest', id, st.cap); say('Opened Pinterest with the photo, caption and link.');
          return;
        }
        sendOne('pinterest');
      },
      instagram: function () { sendOne('instagram'); },
      facebook: function () { sendOne('facebook'); },
      more: function () { sendOne('share_sheet'); },
      download: function () {
        if (!blob) { say('One moment, the picture is still being drawn.'); return; }
        var id = shareId(), link = NS.link(D, st, 'download', id);
        save(blob, fileName(st)); copy(captionWith(link)); track(D, st, 'download', id, st.cap); say('Downloaded. The caption and its link were copied.');
      },
      copy_link: function () {
        var id = shareId(), link = NS.link(D, st, 'copy_link', id);
        copy(link).then(function () { say('Link copied.'); }); track(D, st, 'copy_link', id, st.cap);
      },
      deck_add: function () {
        if (editing < 0 && deck.length >= DECK_MAX) { say('A deck holds ' + DECK_MAX + ' pictures (Instagram’s most).'); return; }
        var sel = snapshot();
        if (deck.length && deck[0].sel.format !== sel.format) sel.format = deck[0].sel.format;
        var cv = document.createElement('canvas');
        NS.render(cv, NS.buildSpec(D, sel), D.fonts).then(function () { return toBlob(cv); }).then(function (b) {
          var th = document.createElement('canvas'), k = 120 / cv.height; th.width = Math.round(cv.width * k); th.height = 120;
          th.getContext('2d').drawImage(cv, 0, 0, th.width, th.height);
          var card = { sel: sel, blob: b, thumb: th.toDataURL('image/jpeg', 0.8) }, was = editing;
          if (was >= 0) { deck[was] = card; editing = -1; } else deck.push(card);
          showDeck();
          var f = FORMATS.filter(function (x) { return x.key === sel.format; })[0];
          say((was >= 0 ? 'Card ' + (was + 1) + ' saved (' : 'Added (') + deck.length + ' in the deck' + (sel.format !== st.format ? ', at the deck’s size, ' + f.name + ' ' + f.ratio : '') + ').');
        });
      },
      deck_share: function () {
        if (!deck.length) return;
        var id = shareId(), sel = deck[0].sel, link = NS.link(D, sel, 'instagram', id, deck.length), cap = captionWith(link);
        var files = deck.map(function (d, i) { return new File([d.blob], fileName(d.sel, i + 1), { type: 'image/jpeg' }); });
        var ok = false; try { ok = canFiles && navigator.canShare({ files: files }); } catch (e) {}
        copy(cap);
        if (ok) {
          navigator.share({ files: files, text: cap }).then(function () { track(D, sel, 'deck', id, st.cap, deck.length); say('Shared the deck of ' + files.length + '. The caption and link were copied too.'); })
            .catch(function (e) { if (e && e.name !== 'AbortError') { files.forEach(function (f) { save(f, f.name); }); say('Downloaded the ' + files.length + ' pictures. The caption and link were copied.'); } });
        } else {
          files.forEach(function (f, i) { setTimeout(function () { save(f, f.name); }, i * 350); });
          track(D, sel, 'deck_download', id, st.cap, deck.length);
          say('This browser can’t hand several pictures to an app, so the ' + files.length + ' were downloaded and the caption and link copied: post them together from the Instagram app.');
        }
      },
      deck_clear: function () { deck = []; editing = -1; showDeck(); say('The deck is empty.'); }
    };
    function showDeck() {
      q('.nw-soc__deck').innerHTML = deck.map(function (d, i) {
        var sk = SKINS.filter(function (x) { return x.key === d.sel.skin; })[0];
        return '<span class="nw-soc__card' + (i === editing ? ' is-editing' : '') + '"><button type="button" class="nw-soc__cardpic" data-sedit="' + i + '" title="Change this card: its skin, style, photo or tags"><img src="' + d.thumb + '" alt="Deck picture ' + (i + 1) + '"><span>' + (i + 1) + (sk && sk.key !== 'none' ? ' · ' + esc(sk.name) : '') + '</span></button><button type="button" class="nw-soc__del" data-sdel="' + i + '" aria-label="Remove picture ' + (i + 1) + '">&times;</button></span>';
      }).join('');
      q('[data-sgo="deck_add"]').textContent = editing >= 0 ? 'Save card ' + (editing + 1) : 'Add this picture';
      q('[data-sgo="deck_share"]').disabled = q('[data-sgo="deck_clear"]').disabled = !deck.length;
      q('[data-sgo="deck_share"]').textContent = deck.length ? 'Share the deck (' + deck.length + ')' : 'Share the deck';
    }

    root.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b || !root.contains(b)) return;
      if (b.dataset.sf != null) { st.finish = +b.dataset.sf; st.photo = 0; thumbs(); caption(); draw(); }
      else if (b.dataset.sp != null) { st.photo = +b.dataset.sp; draw(); }
      else if (b.dataset.sfmt) { st.format = b.dataset.sfmt; draw(); }
      else if (b.dataset.sst) { st.style = b.dataset.sst; draw(); }
      else if (b.dataset.sskin) { st.skin = b.dataset.sskin; caption(); draw(); }
      else if (b.dataset.stag) { st.tags[b.dataset.stag] = !st.tags[b.dataset.stag]; draw(); }
      else if (b.dataset.scap) { st.cap = b.dataset.scap; caption(true); marks(); }
      else if (b.dataset.sdel != null) { var di = +b.dataset.sdel; deck.splice(di, 1); if (editing === di) editing = -1; else if (editing > di) editing--; showDeck(); }
      else if (b.dataset.sedit != null) {   // load a card back: change its skin (or anything) and save it over the card
        var ei = +b.dataset.sedit, cs = deck[ei].sel;
        if (editing === ei) { editing = -1; showDeck(); say(''); return; }
        editing = ei; st.finish = cs.finish; st.photo = cs.photo; st.format = cs.format; st.style = cs.style; st.skin = cs.skin; st.tags = { swatch: cs.tags.swatch, ar: cs.tags.ar, partner: !!cs.tags.partner };
        thumbs(); caption(); draw(); showDeck(); say('Card ' + (ei + 1) + ' loaded: change it, then Save card ' + (ei + 1) + '.');
      }
      else if (b.dataset.spedit) { partnerLine(true); var inp = q('.nw-soc__pcode'); if (inp) inp.focus(); }
      else if (b.dataset.spcancel) partnerLine();
      else if (b.dataset.spsave) {
        var raw = (q('.nw-soc__pcode') || {}).value || '', code = cleanRef(raw);
        if (!code) { say(raw.trim() ? 'That does not look like a partner code: letters, numbers, - and _ only.' : 'Type your partner code first.'); return; }
        setPartnerRef(code); st.ref = code; deck.forEach(function (d) { d.sel.ref = code; }); partnerLine(); draw(); say('Saved. Your shares carry the code ' + code + '.');
      }
      else if (b.dataset.spclear) { setPartnerRef(''); st.ref = ''; deck.forEach(function (d) { d.sel.ref = ''; d.sel.tags.partner = false; }); partnerLine(); draw(); say('Partner code removed from this device.'); }
      else if (b.dataset.sgo && GO[b.dataset.sgo]) { say(''); GO[b.dataset.sgo](); }
    });
    text.addEventListener('input', function () { st.edited = true; });
    root.addEventListener('keydown', function (e) { if (e.key === 'Enter' && e.target.classList && e.target.classList.contains('nw-soc__pcode')) { e.preventDefault(); var sv = q('[data-spsave]'); if (sv) sv.click(); } });
    finishChips(); thumbs(); caption(true); partnerLine(); showDeck(); draw();
  };

  // mount when opened (the Social row's details, or the special-order page's dialog), never before
  function hook(el) {
    var root = el.querySelector('.nw-soc');
    if (!root) return;
    if (el.open) NS.mount(root);
    el.addEventListener('toggle', function () { if (el.open) NS.mount(root); });
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('[data-nw-soc-open]');
    if (!a) return;
    var dlg = document.getElementById(a.getAttribute('data-nw-soc-open'));
    if (!dlg) return;
    e.preventDefault();
    if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
    NS.mount(dlg.querySelector('.nw-soc'));
  });
  function scan() { Array.prototype.forEach.call(document.querySelectorAll('[data-nw-soc-row]'), function (r) { var d = r.closest('details'); if (d && !d.__nwSoc) { d.__nwSoc = true; hook(d); } }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
})();
